import { useState } from "react";
import { Merge, Plus, Split, Trash2, X } from "lucide-react";
import { RulePart, VerityRule } from "./types";

interface SplitMergeDialogProps {
  mode: "split" | "merge";
  /** One rule for a split; two or more for a merge. */
  rules: VerityRule[];
  onCancel: () => void;
  onApply: (parts: RulePart[]) => Promise<void>;
}

/**
 * Correcting the level a requirement was understood at.
 *
 * Both benchmarks said this is the correction reviewers make most: not "that value is wrong" but
 * "that is two requirements, not one" — or the reverse. Doing it by excluding a candidate and typing a
 * replacement would work, and would throw away the link back to the sentence and the record of who
 * decided it. So it is a first-class action, and the source stays attached to whatever comes out.
 */
const SplitMergeDialog = ({ mode, rules, onCancel, onApply }: SplitMergeDialogProps) => {
  const source = rules[0]!;

  const [parts, setParts] = useState<RulePart[]>(
    mode === "split"
      ? [
          { subject: source.subject, requirement: source.requirement },
          { subject: source.subject, requirement: "" },
        ]
      : [
          {
            subject: source.subject,
            // A merge is usually a compound obligation, so the default offers it as one.
            requirement: rules.map((r) => r.requirement).join(" and "),
            predicate: {
              kind: "compound",
              parts: rules.map((r) => r.requirement),
            },
          },
        ],
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (index: number, field: keyof RulePart, value: string) =>
    setParts((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));

  const apply = async () => {
    const filled = parts.filter((p) => p.subject.trim() && p.requirement.trim());
    if (mode === "split" && filled.length < 2) {
      return setError("A split needs at least two requirements, each with a subject and a requirement.");
    }
    if (filled.length === 0) return setError("Give the merged requirement a subject and a requirement.");

    setError(null);
    setSaving(true);
    try {
      await onApply(filled);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That could not be applied.");
      setSaving(false);
    }
  };

  return (
    <div className="vr-backdrop vr-backdrop-top" role="dialog" aria-modal="true">
      <div className="vr-modal">
        <div className="vr-modal-head">
          <div>
            <h3>
              {mode === "split" ? "Split this requirement" : `Merge ${rules.length} requirements`}
            </h3>
            <p>
              {mode === "split"
                ? "Two things that must each be satisfied are two requirements."
                : "One act by one party on one thing is a single compound requirement."}
            </p>
          </div>
          <button className="vr-iconbtn" onClick={onCancel} aria-label="Close" disabled={saving}>
            <X size={16} />
          </button>
        </div>

        <div className="vr-modal-body">
          {error && <div className="vr-form-error">{error}</div>}

          {/* The source sentence, once, above everything. It does not change — whatever comes out of
              this dialog cites exactly what went in. */}
          <blockquote className="vr-quote">
            <span className="vr-quote-text">“{source.sourceQuote}”</span>
            <span className="vr-quote-src">
              §{source.sectionNumber} · printed page {source.printedPage ?? "—"}
            </span>
          </blockquote>

          {mode === "merge" && (
            <div className="vr-merge-list">
              {rules.map((r) => (
                <div key={r.ruleId} className="vr-merge-item">
                  <span className="vr-rule-id">{r.displayId}</span>
                  <span>
                    {r.subject} — {r.requirement}
                  </span>
                </div>
              ))}
            </div>
          )}

          {parts.map((part, i) => (
            <div key={i} className="vr-part">
              <div className="vr-part-head">
                <span>{mode === "split" ? `Requirement ${i + 1}` : "Merged requirement"}</span>
                {mode === "split" && parts.length > 2 && (
                  <button
                    className="vr-rowbtn vr-danger vr-icononly"
                    onClick={() => setParts((prev) => prev.filter((_, j) => j !== i))}
                    aria-label="Remove"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="vr-field-grid">
                <div className="vr-field">
                  <label>Subject</label>
                  <input
                    className="vr-input"
                    value={part.subject}
                    onChange={(e) => set(i, "subject", e.target.value)}
                    placeholder="What the requirement is about"
                  />
                </div>
                <div className="vr-field">
                  <label>Requirement</label>
                  <input
                    className="vr-input"
                    value={part.requirement}
                    onChange={(e) => set(i, "requirement", e.target.value)}
                    placeholder="What is required"
                  />
                </div>
              </div>
            </div>
          ))}

          {mode === "split" && (
            <button
              className="vr-rowbtn"
              onClick={() => setParts((prev) => [...prev, { subject: source.subject, requirement: "" }])}
            >
              <Plus size={12} /> Another requirement
            </button>
          )}
        </div>

        <div className="vr-modal-foot">
          <button className="vr-btn vr-btn-ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="vr-btn vr-btn-glow" onClick={() => void apply()} disabled={saving}>
            {mode === "split" ? <Split size={14} /> : <Merge size={14} />}
            {saving ? "Applying…" : mode === "split" ? "Split" : "Merge"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitMergeDialog;
